import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  FormGroupDirective,
  NgForm,
  Validators,
  FormControl,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import {
  MatAutocompleteTrigger,
  MatAutocomplete,
} from '@angular/material/autocomplete';
import { ErrorStateMatcher, MatOption } from '@angular/material/core';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { Observable, Subscription, map, startWith } from 'rxjs';
import { EnumAffinities } from 'src/app/models/gum-models/enum-affinities';
import { EnumScopes } from 'src/app/models/enum-scopes';
import { EnumType } from 'src/app/models/gum-models/enum-type';
import { ExternalParameters } from 'src/app/models/external-parameters';
import { Property } from 'src/app/models/property';
import { PropertyDefinition } from 'src/app/models/gum-models/property-definition';
import { ExternalsParametersService } from 'src/app/services/external-parameters.service';
import { PropertyDefinitonsService } from 'src/app/services/gumservices/property-definitons.service';
import { FormArrayFilterPipePipe } from 'src/app/pipes/form-array-filter-pipe.pipe';
import { MatSelect } from '@angular/material/select';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatInput } from '@angular/material/input';
import {
  MatFormField,
  MatLabel,
  MatSuffix,
  MatPrefix,
} from '@angular/material/form-field';
import { AsyncPipe, DatePipe } from '@angular/common';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  NgxMatDatepickerInput,
  NgxMatDatepickerToggle,
  NgxMatDatetimepicker,
  NgxMatTimepickerComponent,
} from '@ngxmc/datetime-picker';

export class ErrorMatcher implements ErrorStateMatcher {
  isErrorState(
    control: UntypedFormControl | null,
    form: FormGroupDirective | NgForm | null
  ): boolean {
    const isSubmitted = form && form.submitted;
    return !!(
      control &&
      control.invalid &&
      (control.dirty || control.touched || isSubmitted)
    );
  }
}

export class PropertyItem {
  name!: string;
  display!: string;
  isSelected!: boolean;
}
@Component({
  selector: 'app-gum-properties',
  templateUrl: './gum-properties.component.html',
  styleUrl: './gum-properties.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatFormField,
    MatLabel,
    MatInput,
    MatAutocompleteTrigger,
    ReactiveFormsModule,
    MatAutocomplete,
    MatOption,
    MatCheckbox,
    MatIconButton,
    MatTooltip,
    MatIcon,
    FormsModule,
    MatSuffix,
    MatSelect,
    MatPrefix,
    AsyncPipe,
    FormArrayFilterPipePipe,
    MatDatepickerModule,
    NgxMatDatetimepicker,
    NgxMatDatepickerInput,
    NgxMatDatepickerToggle,
    NgxSpinnerModule,
  ],
})
export class GumPropertiesComponent implements OnInit, OnDestroy {
  private spinnerService = inject(NgxSpinnerService);
  propertiesForm: UntypedFormGroup;
  availableProperties!: PropertyDefinition[];
  unassignedProperties: Array<PropertyItem> = [];
  selectedProperties: string[] = [];
  managedScope = EnumScopes.Managed;
  propertyListControl = new FormControl();
  propertyList!: Observable<PropertyItem[]>;

  matcher = new ErrorMatcher();

  searchProperty = '';

  @Input() properties!: Property[];

  @Input() isProfile = false;

  @Input() affinity: EnumAffinities = EnumAffinities.Any;

  @Output() changed = new EventEmitter<Property[]>();

  @Output() isValid = new EventEmitter<boolean>();

  @ViewChild('propertyInput') propertyInput!: ElementRef<HTMLInputElement>;

  isExternal = false;
  externalParameters!: ExternalParameters;
  externalParametersChange!: Subscription;

  private propertiesNotAffected: Property[] = [];

  constructor(
    private formbuilder: UntypedFormBuilder,
    private propertyDefinitionsService: PropertyDefinitonsService,
    private externalsParametersService: ExternalsParametersService
  ) {
    this.propertiesForm = this.formbuilder.group({
      propertiesGroup: this.formbuilder.array([]),
    });
  }

  ngOnInit() {
    this.loadDatas();

    this.externalParametersChange =
      this.externalsParametersService.externalParametersChange$.subscribe(
        (parameters) => {
          if (parameters) {
            this.externalParameters = parameters;
            this.isExternal = true;
            this.properties = this.properties.filter((a) =>
              parameters.properties.includes(a.name)
            );
          }
        }
      );
  }

  ngOnDestroy() {
    this.externalParametersChange.unsubscribe();
  }
  onChanges() {
    this.spinnerService.show('spinner');

    this.properties = [];

    const formArray = this.propertiesForm.get(
      'propertiesGroup'
    ) as UntypedFormArray;

    formArray.controls.forEach((control) => {
      const value = control.getRawValue();
      const type = value.type;

      const prop: Property = {
        name: value.name,
        legacy: value.legacy,
        value: '',
      };

      if (type === EnumType.Date) {
        prop.value = value.value
          ? new DatePipe('en').transform(value.value, 'yyyy-MM-dd') ?? ''
          : '';
      } else if (type === EnumType.Datetime) {
        prop.value = value.value
          ? new DatePipe('en').transform(value.value, 'yyyy-MM-dd HH:mm:ss') ??
            ''
          : '';
      } else {
        prop.value = value.value?.toString() ?? '';
      }

      this.properties.push(prop);
    });

    this.properties = [...this.properties, ...this.propertiesNotAffected];

    this.changed.emit(this.properties);
    this.isValid.emit(this.propertiesForm.valid);

    this.spinnerService.hide('spinner');
  }

  loadDatas() {
    this.spinnerService.show('spinner');

    this.unassignedProperties.splice(0, this.unassignedProperties.length);

    this.propertyDefinitionsService
      .getPropertyDefinitions()
      .subscribe((confProperties) => {
        this.availableProperties = this.getAvailableProperties(
          this.affinity,
          confProperties
        );

        this.availableProperties.forEach((p) => {
          const property: PropertyItem = {
            name: p.name,
            display: p.display?.[0]?.value ?? p.name,
            isSelected: false,
          };
          this.unassignedProperties.push(property);
        });

        const propertyForm: UntypedFormGroup[] = [];
        this.propertiesNotAffected = [];

        this.properties
          .sort((a, b) => a.name.localeCompare(b.name))
          .forEach((propertyUser) => {
            const indexUnassignedProperty = this.unassignedProperties.findIndex(
              (property) => property.name === propertyUser.name
            );

            if (indexUnassignedProperty >= 0) {
              this.unassignedProperties.splice(indexUnassignedProperty, 1);
            }

            const propertydefinition = this.availableProperties.find(
              (p) => p.name === propertyUser.name
            );

            if (propertydefinition) {
              if (
                propertydefinition.type === EnumType.Datetime &&
                propertyUser.value
              ) {
                propertyUser.value =
                  new DatePipe('en').transform(
                    propertyUser.value,
                    'yyyy-MM-ddTHH:mm:ss'
                  ) ?? '';
              }

              propertyForm.push(
                this.formbuilder.group({
                  name: [propertyUser.name],
                  translateName: [propertyUser.name], // tu peux supprimer complètement ce champ si non utilisé
                  value: [
                    {
                      value:
                        propertydefinition.type === EnumType.Check
                          ? Boolean(JSON.parse(propertyUser.value))
                          : propertydefinition.type === EnumType.MList
                          ? propertyUser.value.split(',')
                          : propertyUser.value ??
                            propertydefinition.default ??
                            '',
                      disabled:
                        propertydefinition.type === EnumType.Date ||
                        propertydefinition.type === EnumType.Datetime ||
                        (propertydefinition.readonly && propertyUser.legacy) ||
                        (this.isProfile &&
                          propertydefinition.scope === EnumScopes.Managed),
                    },
                    Validators.pattern(propertydefinition.constraint),
                  ],
                  type: [propertydefinition.type],
                  values: [propertydefinition.values],
                  scope: [propertydefinition.scope],
                  readonly: [
                    { value: propertydefinition.readonly, disabled: true },
                  ],
                  legacyable: [
                    { value: propertydefinition.legacyable, disabled: true },
                  ],
                  legacy: [{ value: propertyUser.legacy, disabled: true }],
                })
              );
            } else {
              this.propertiesNotAffected.push(propertyUser);
            }
          });

        this.propertiesForm = this.formbuilder.group({
          propertiesGroup: this.formbuilder.array(propertyForm),
        });

        this.propertiesForm.markAllAsTouched();
        this.isValid.emit(this.propertiesForm.valid);

        this.unassignedProperties.sort((a, b) =>
          a.display.localeCompare(b.display)
        );

        this.propertyList = this.propertyListControl.valueChanges.pipe(
          startWith(null),
          map((property: string | null) =>
            property
              ? this.filterProperty(property)
              : this.unassignedProperties.slice()
          )
        );

        this.spinnerService.hide('spinner');
      });
  }

  /** Click sur le bouton pour ajouter une propriété */
  addProperty(propertiesName: string[]) {
    propertiesName.forEach((propertyName) => {
      const indexUnassignedProperty = this.unassignedProperties.findIndex(
        (p) => p.name === propertyName
      );

      if (indexUnassignedProperty >= 0) {
        this.unassignedProperties.splice(indexUnassignedProperty, 1);
      }

      const propertiesGroup = this.propertiesForm.get(
        'propertiesGroup'
      ) as UntypedFormArray;

      const propertydefinition = this.availableProperties.find(
        (p) => p.name === propertyName
      );

      if (!propertydefinition) {
        console.warn(`Property definition not found for '${propertyName}'`);
        return;
      }

      let dateValue: Date | null = null;

      if (
        propertydefinition.type === EnumType.Date ||
        propertydefinition.type === EnumType.Datetime
      ) {
        if (propertydefinition.default) {
          const raw = propertydefinition.default;
          dateValue =
            propertydefinition.type === EnumType.Date
              ? new Date(new DatePipe('en').transform(raw, 'yyyy-MM-dd')!)
              : new Date(
                  new DatePipe('en').transform(raw, 'yyyy-MM-dd HH:mm')!
                );
        } else {
          dateValue = new Date();
        }
      }

      propertiesGroup.push(
        this.formbuilder.group({
          name: [propertyName],
          translateName: [propertyName],
          value: [
            {
              value:
                propertydefinition.type === EnumType.Check
                  ? Boolean(JSON.parse(propertydefinition.default ?? 'false'))
                  : propertydefinition.type === EnumType.Date ||
                    propertydefinition.type === EnumType.Datetime
                  ? dateValue
                  : propertydefinition.default ?? '',
              disabled:
                propertydefinition.type === EnumType.Date ||
                propertydefinition.type === EnumType.Datetime,
            },
            Validators.pattern(propertydefinition.constraint),
          ],
          type: [propertydefinition.type],
          values: [propertydefinition.values],
          scope: [
            indexUnassignedProperty >= 0
              ? propertydefinition.scope
              : EnumScopes.User,
          ],
          readonly: [{ value: propertydefinition.readonly, disabled: true }],
          legacyable: [
            { value: propertydefinition.legacyable, disabled: true },
          ],
          legacy: [{ value: false, disabled: true }],
        })
      );
    });

    this.selectedProperties = [];
    this.onChanges();
  }

  /** Click sur le bouton pour supprimer une propriété */
  deleteProperty(propertiesName: string, index: number) {
    const currentProperties = this.propertiesForm.get(
      'propertiesGroup'
    ) as UntypedFormArray;

    currentProperties.removeAt(index);

    const confProperty = this.availableProperties.find(
      (p) => p.name === propertiesName
    );

    if (confProperty) {
      const property: PropertyItem = {
        name: confProperty.name,
        display: confProperty.display?.[0]?.value ?? confProperty.name,
        isSelected: false,
      };

      this.unassignedProperties.push(property);
    }

    this.unassignedProperties.sort((a, b) =>
      a.display.localeCompare(b.display)
    );

    this.onChanges();
  }

  getAvailableProperties(
    affinity: EnumAffinities,
    propertyDefinitions: PropertyDefinition[]
  ): PropertyDefinition[] {
    switch (affinity) {
      case EnumAffinities.User:
        return propertyDefinitions.filter(
          (p) =>
            (p.scope === EnumScopes.User || p.scope === EnumScopes.Managed) &&
            (p.affinity === EnumAffinities.User ||
              p.affinity === EnumAffinities.Any)
        );
        break;

      case EnumAffinities.Group:
        return propertyDefinitions.filter(
          (p) =>
            (p.scope === EnumScopes.User || p.scope === EnumScopes.Managed) &&
            (p.affinity === EnumAffinities.Group ||
              p.affinity === EnumAffinities.Any)
        );
        break;

      case EnumAffinities.Organization:
        return propertyDefinitions.filter(
          (p) =>
            (p.scope === EnumScopes.User || p.scope === EnumScopes.Managed) &&
            (p.affinity === EnumAffinities.Organization ||
              p.affinity === EnumAffinities.Any)
        );
        break;

      case EnumAffinities.Member:
        return propertyDefinitions.filter(
          (p) =>
            (p.scope === EnumScopes.User || p.scope === EnumScopes.Managed) &&
            (p.affinity === EnumAffinities.Member ||
              p.affinity === EnumAffinities.Any)
        );
        break;

      case EnumAffinities.Application:
        return propertyDefinitions.filter(
          (p) =>
            (p.scope === EnumScopes.User || p.scope === EnumScopes.Managed) &&
            (p.affinity === EnumAffinities.Application ||
              p.affinity === EnumAffinities.Any)
        );
        break;

      default:
        return propertyDefinitions;
        break;
    }
  }

  getScopeLabel(scope: string): string {
    switch (scope) {
      case 'User':
        return 'Utilisateur';
      case 'Managed':
        return 'Managé';
      case 'Organization':
        return 'Organisation';
      default:
        return scope;
    }
  }

  getPropertyTranslate(propertiesName: string): string {
    return propertiesName;
  }

  resetSearch() {
    this.searchProperty = '';
  }

  toggleSelection(property: PropertyItem) {
    property.isSelected = !property.isSelected;
    if (property.isSelected) {
      this.selectedProperties.push(property.name);
    } else {
      const i = this.selectedProperties.findIndex((x) => x === property.name);
      this.selectedProperties.splice(i, 1);
    }
  }

  private filterProperty(value: string): PropertyItem[] {
    const filterValue = value.toLowerCase();

    return this.unassignedProperties.filter(
      (property) => property.display.toLowerCase().indexOf(filterValue) >= 0
    );
  }

  get propertiesControls(): UntypedFormGroup[] {
    const group = this.propertiesForm.get('propertiesGroup');
    return group instanceof UntypedFormArray
      ? (group.controls as UntypedFormGroup[])
      : [];
  }
}
